import { useUserRegionData } from 'flow/hooks/region-lookup/useUserRegionData';
import { useAppTimezone } from 'flow/hooks/region-lookup/useAppTimezone';
import DropdownMenuComponent from '../ui-components/DropdownMenuComponent';
import useAppContext from 'flow/hooks/useAppContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RegionType } from 'flow/types/region-lookup/UserRegion';

interface UserRegionDropdownProps {
    isModal?: boolean;
}

const UserRegionDropdown = ({ isModal }: UserRegionDropdownProps) => {
    const { regionData, updateRegionData, allRegions } = useUserRegionData();
    const { location } = useAppTimezone();
    const { i18next } = useAppContext();
    const [selectedRegion, setSelectedRegion] = useState<RegionType | null>(
        regionData?.region || null,
    );
    const prevUrlRef = useRef<string | undefined>(undefined);

    const regionOptions = useMemo(
        () =>
            allRegions
                ? allRegions.map((region) => ({
                      key: region.region,
                      label: region.displayName || region.region,
                  }))
                : [],
        [allRegions],
    );

    const preselectedRegion = useMemo(() => {
        const regionMap = new Map(allRegions?.map((r) => [r.region, r]));
        const fallbacks = [selectedRegion, location, regionData?.region];
        const matched = fallbacks.find((key) => key && regionMap.has(key));
        return matched ? regionMap.get(matched) : allRegions?.[0] || undefined;
    }, [regionData?.region, location, selectedRegion, allRegions]);

    useEffect(() => {
        const newUrl = preselectedRegion?.url;
        if (newUrl && newUrl !== prevUrlRef.current) {
            prevUrlRef.current = newUrl;
            updateRegionData({
                socialSelectedUrl: newUrl,
                isOidcLogin: false,
                region: preselectedRegion.region,
            });
        }
    }, [preselectedRegion?.url, preselectedRegion?.region, updateRegionData]);

    const onSelectRegion = (key: string) =>
        setSelectedRegion(key as RegionType);

    return (
        <div className="flex items-center">
            {!isModal && (
                <label className="text-sm font-semibold">
                    {i18next.t('general.region', 'Region')}
                </label>
            )}
            <DropdownMenuComponent
                title={preselectedRegion?.displayName}
                options={regionOptions}
                onItemSelected={onSelectRegion}
                menuButtonClassName={!isModal ? 'w-auto !bg-transparent' : ''}
                menuItemClassName="text-sm"
                className="flex-1"
            />
        </div>
    );
};

export default UserRegionDropdown;
